import { v4 as uuid } from "uuid";
import { z } from "zod";
import { PackageType } from "../../entities/contractorPackage";
import { ProjectAccessLevel, ProjectSector, ReportCadence } from "../../entities/project";
import { WORKFORCE_PROFILE_FULL_TIME_MONTHLY_HOURS } from "../../entities/workforceProfile";
import { dateToString, getDefaultProjectEndDate } from "../../utils/date";
import { createDefaultContactInfo, contactInfoLineItemSchema } from "../contactInfo";
import { emissionFactorLineItemSchema } from "../emissionFactor";
import { projectEnvironmentalCommitmentLineItemSchema } from "../environmentalCommitment";
import { projectEnvironmentalTargetLineItemSchema } from "../environmentalTarget";
import { createDefaultFinancial, financialLineItemSchema } from "../financial";
import { projectFrameworkLineItemSchema } from "../projectFramework";
import { projectLabourHourCommitmentLineItemSchema } from "../projectLabourHourCommitment";
import { projectLocalContentLineItemSchema } from "../projectLocalContent";
import { projectPolicyLineItemSchema } from "../projectPolicy";
import { projectSocialSpendCommitmentLineItemSchema } from "../projectSocialSpendCommitment";
export const createDefaultProject = (organisation) => {
    return {
        archived: false,
        closedDate: undefined,
        collaborators: [],
        commitmentsEnvironmental: [],
        commitmentsHours: [],
        commitmentsSpend: [],
        created: new Date(),
        emissionFactors: [],
        estimatedProgress: 0,
        financial: createDefaultFinancial(),
        id: uuid(),
        isClosed: false,
        isShared: false,
        localContents: [],
        organisationDisplayName: organisation?.displayName || "",
        organisationId: organisation?.id || "",
        organisationLogo: organisation?.organisationLogo || "",
        organisationName: organisation?.organisationName || "",
        policies: [],
        projectAccessLevel: ProjectAccessLevel.Admin,
        projectAddress: createDefaultContactInfo(),
        projectEndDate: getDefaultProjectEndDate(),
        projectFrameworks: [],
        projectName: "",
        projectStartDate: dateToString(new Date()),
        reportCadence: ReportCadence.CurrentMonth,
        reportingDueDate: 14,
        sector: ProjectSector.Building,
        targetsEnvironmental: [],
        workforceProfileFullTimeMonthlyHours: WORKFORCE_PROFILE_FULL_TIME_MONTHLY_HOURS
    };
};
export var ProjectStatus;
(function (ProjectStatus) {
    ProjectStatus["Active"] = "ACTIVE";
    ProjectStatus["Archived"] = "ARCHIVED";
})(ProjectStatus || (ProjectStatus = {}));
export const newProjectToProjectDetail = (project) => {
    return {
        ...project,
        // A brand-new project has no work packages, so it has no collaborators.
        collaboratorDetails: [],
        contractorPackageId: project.id,
        isAccepted: true,
        packageType: PackageType.HeadContractorPackage,
        projectContactInfoId: project.projectAddress.id
    };
};
export const projectLineItemSchema = z.object({
    archived: z.boolean(),
    closedDate: z.string().optional(),
    collaborators: z.array(z.string()),
    commitmentsEnvironmental: z.array(projectEnvironmentalCommitmentLineItemSchema),
    commitmentsHours: z.array(projectLabourHourCommitmentLineItemSchema),
    commitmentsSpend: z.array(projectSocialSpendCommitmentLineItemSchema),
    created: z.date(),
    emissionFactors: z.array(emissionFactorLineItemSchema),
    /** Absent for projects that never turned environmental on. */
    estimatedProgress: z.number(),
    financial: financialLineItemSchema,
    hammertechProjectId: z.string().optional(),
    id: z.string(),
    isClosed: z.boolean(),
    isShared: z.boolean().optional(),
    localContents: z.array(projectLocalContentLineItemSchema),
    /**
     * Label to render wherever the organisation is named. Canonical form of
     * `organisationName` with shouted casing normalised, falling back to it.
     * Keep using the raw name on contracts, reports and exports.
     */
    organisationDisplayName: z.string(),
    organisationId: z.string(),
    organisationLogo: z.string(),
    organisationName: z.string(),
    policies: z.array(projectPolicyLineItemSchema),
    projectAccessLevel: z.enum(ProjectAccessLevel),
    projectAddress: contactInfoLineItemSchema,
    projectEndDate: z.string(),
    projectFrameworks: z.array(projectFrameworkLineItemSchema).optional(),
    projectName: z.string(),
    projectStartDate: z.string(),
    reportCadence: z.enum(ReportCadence),
    reportExists: z.boolean().optional(),
    reportingDueDate: z.number(),
    sector: z.enum(ProjectSector),
    targetsEnvironmental: z.array(projectEnvironmentalTargetLineItemSchema),
    userPackageId: z.string().optional(),
    /** Hours a 100% workforce-profile month means on this project. */
    workforceProfileFullTimeMonthlyHours: z.number().optional()
});
export { ProjectCommmitmentGenerationSource } from "./enums";
